import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import {
  mapQueryResult,
  mapReleaseDetailDto,
  mapReleaseListItemDto,
} from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { mergeProjectFilters } from '@/shared/data/queryFilters';
import {
  buildEqFilter,
  buildSlugFilters,
  mergeFilters,
  type GridFilters,
} from '@/ui/composites/grid/gridFilters';
import type {
  ReleaseDetailDto,
  ReleaseListItemDto,
  ReleasePlannedCalendarDto,
  ReleaseReleasedCalendarDto,
  ReleaseSummaryDto,
} from '@flowqa/shared';
import type { QueryFilter, QueryResult, QuerySort } from '@flowqa/shared/query';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const queryReleases = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    includeSummary?: boolean;
    include?: ReleaseInclude;
  },
): Promise<QueryResult<ReleaseListItemDto>> => {
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  try {
    const result = await apiClient.post<QueryResult<ReleaseListItemDto>>(
      'releases/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts: input?.sorts ?? [{ field: 'createdAt', direction: 'desc' }],
        filters: combinedFilters,
        includeSummary: input?.includeSummary,
        include: input?.include,
      },
      { retries: 1, orgId: organisationId ?? undefined },
    );
    return mapQueryResult(result, mapReleaseListItemDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryRelatedReleases = async (
  projectId: string | null | undefined,
  organisationId: string | null | undefined,
  related: { featureIds?: string[]; suiteIds?: string[]; testIds?: string[] },
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    include?: ReleaseInclude;
  },
): Promise<QueryResult<ReleaseListItemDto>> => {
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId ?? null);
  const payload = {
    page: input?.page ?? 1,
    pageSize: input?.pageSize ?? 50,
    sorts: input?.sorts ?? [{ field: 'createdAt', direction: 'desc' }],
    filters: combinedFilters,
    include: input?.include,
    related: {
      featureIds:
        related.featureIds && related.featureIds.length > 0 ? related.featureIds : undefined,
      suiteIds: related.suiteIds && related.suiteIds.length > 0 ? related.suiteIds : undefined,
      testIds: related.testIds && related.testIds.length > 0 ? related.testIds : undefined,
    },
  };
  try {
    const result = await apiClient.post<QueryResult<ReleaseListItemDto>>(
      'releases/related',
      payload,
      {
        retries: 1,
        orgId: organisationId ?? undefined,
      },
    );
    return mapQueryResult(result, mapReleaseListItemDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryReleaseSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: { page: number; pageSize: number; sorts: QuerySort[]; filters: QueryFilter[] },
): Promise<ReleaseSummaryDto> => {
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  try {
    return await apiClient.post<ReleaseSummaryDto>(
      'releases/summary',
      {
        summaryScope: 'full_filtered_scope',
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts: input?.sorts ?? [],
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryReleasePlannedCalendar = async (
  projectId: string | null | undefined,
  organisationId: string | null | undefined,
  input: { start: string; end: string },
): Promise<ReleasePlannedCalendarDto> => {
  try {
    return await apiClient.post<ReleasePlannedCalendarDto>(
      'releases/calendar/planned',
      { start: input.start, end: input.end, projectId: projectId ?? undefined },
      { retries: 1, orgId: organisationId ?? undefined },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryReleaseReleasedCalendar = async (
  projectId: string | null | undefined,
  organisationId: string | null | undefined,
  input: { start: string; end: string },
): Promise<ReleaseReleasedCalendarDto> => {
  try {
    return await apiClient.post<ReleaseReleasedCalendarDto>(
      'releases/calendar/released',
      { start: input.start, end: input.end, projectId: projectId ?? undefined },
      { retries: 1, orgId: organisationId ?? undefined },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getReleaseBySlug = async (
  slug: string | null,
  projectId?: string | null,
  organisationId?: string | null,
): Promise<ReleaseDetailDto | null> => {
  if (!slug) {
    return null;
  }
  try {
    const payload = await apiClient.post<QueryResult<ReleaseListItemDto>>(
      'releases/query',
      {
        page: 1,
        pageSize: 1,
        filters: mergeFilters(buildSlugFilters(slug), buildEqFilter('projectId', projectId)),
        include: { project: true },
      },
      { retries: 1, orgId: organisationId ?? undefined },
    );
    const mapped = mapQueryResult(payload, mapReleaseListItemDto);
    if (mapped.items.length > 0) {
      return await getReleaseById(mapped.items[0].id, organisationId);
    }
    return null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw toApiError(err);
  }
};

export const getReleaseById = async (
  id: string,
  organisationId?: string | null,
): Promise<ReleaseDetailDto> => {
  try {
    const result = await apiClient.get<ReleaseDetailDto>(`releases/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapReleaseDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const createRelease = async (
  input: {
    name: string;
    projectId: string;
    status?: ReleaseDetailDto['status'];
    plannedFor?: string | null;
    releasedAt?: string | null;
    featureIds?: string[];
    suiteIds?: string[];
    testIds?: string[];
    tags?: string[];
  },
  organisationId?: string | null,
): Promise<ReleaseDetailDto> => {
  const token = ensureToken();
  const body = {
    name: input.name.trim(),
    projectId: input.projectId.trim(),
    status: input.status,
    plannedFor: input.plannedFor === undefined ? undefined : input.plannedFor,
    releasedAt: input.releasedAt === undefined ? undefined : input.releasedAt,
    featureIds: input.featureIds,
    suiteIds: input.suiteIds,
    testIds: input.testIds,
    tags: input.tags,
  };
  try {
    const result = await apiClient.post<ReleaseDetailDto>('releases', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapReleaseDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateRelease = async (
  input: {
    id: string;
    name?: string;
    status?: ReleaseDetailDto['status'];
    plannedFor?: string | null;
    releasedAt?: string | null;
    featureIds?: string[];
    suiteIds?: string[];
    testIds?: string[];
    tags?: string[];
  },
  organisationId?: string | null,
): Promise<ReleaseDetailDto> => {
  const token = ensureToken();
  const body = {
    name: input.name?.trim(),
    status: input.status,
    plannedFor: input.plannedFor === undefined ? undefined : input.plannedFor,
    releasedAt: input.releasedAt === undefined ? undefined : input.releasedAt,
    featureIds: input.featureIds,
    suiteIds: input.suiteIds,
    testIds: input.testIds,
    tags: input.tags,
  };
  try {
    const result = await apiClient.patch<ReleaseDetailDto>(`releases/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapReleaseDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export type ReleaseInclude = {
  project?: boolean;
  features?: { ids?: boolean; items?: boolean };
  suites?: { ids?: boolean; items?: boolean };
  tests?: { ids?: boolean; items?: boolean };
  latestSuiteResults?: { counts?: boolean; summaries?: boolean };
  latestTestResults?: { counts?: boolean; summaries?: boolean };
};

export const deleteRelease = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`releases/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
